import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { vocSubmit } from "../../utils/api";

const VocSubmit = () => {
  const [formData, setFormData] = useState({
    serviceSortation: "06",
    name: "",
    phoneNumber: "",
    password: "",
    title: "",
    content: "",
    email: "",
    companyName: "",
  });
  const [fileName, setFileName] = useState("");
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [vocNo, setVocNo] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      const numericValue = value.replace(/[^0-9]/g, "");
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith(".zip")) {
        showAlert("입력 오류", ".zip 파일만 첨부 가능합니다.");
        e.target.value = "";
        setFileName("");
        setUploadFile(null);
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        showAlert("입력 오류", "파일 크기는 20MB 이내여야 합니다.");
        e.target.value = "";
        setFileName("");
        setUploadFile(null);
        return;
      }
      setFileName(file.name);
      setUploadFile(file);
    }
  };

  const showAlert = (title: string, message: string, success = false) => {
    setModalTitle(title);
    setModalMessage(message);
    setIsSuccess(success);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 필수 필드 검증
    if (
      !formData.name ||
      !formData.phoneNumber ||
      !formData.password ||
      !formData.title ||
      !formData.content ||
      !formData.email
    ) {
      showAlert("입력 오류", "필수 항목을 모두 입력해주세요.");
      return;
    }

    // 이메일 형식 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showAlert("입력 오류", "이메일 주소를 올바른 형식으로 입력해주세요.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await vocSubmit(
        {
          title: formData.title,
          content: formData.content,
          name: formData.name,
          password: formData.password,
          phoneNumber: formData.phoneNumber,
          companyName: formData.companyName || "",
          serviceSortation: formData.serviceSortation,
          email: formData.email,
        },
        uploadFile
      );

      setVocNo(response.no);
      showAlert(
        "등록되었습니다.",
        `게시글 번호 : ${response.no}\n\n*게시글 번호는 VOC 내역 조회 시 사용됩니다.`,
        true
      );

      // 폼 초기화
      setFormData({
        serviceSortation: "06",
        name: "",
        phoneNumber: "",
        password: "",
        title: "",
        content: "",
        email: "",
        companyName: "",
      });
      setFileName("");
      setUploadFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error: unknown) {
      console.error("VOC 등록 오류:", error);
      const errorMessage =
        error instanceof Error ? error.message : "알 수 없는 오류가 발생했습니다.";
      showAlert("등록 실패", `문의 등록에 실패했습니다.\n${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main id="main-content">
      <div
        className="mx-auto w-full"
        style={{
          maxWidth: "900px",
          margin: "105px auto",
        }}
      >
        {/* Title Box */}
        <div
          className="flex flex-col sm:flex-row justify-between items-center px-10 about:px-0 pb-[20px] mb-[30px]"
          style={{ borderBottom: "1px solid #e5e5e5" }}
        >
          <div className="mb-[1rem] text-center sm:text-left">
            <h1
              className="inline-block mb-[8px]"
              style={{
                fontSize: "var(--font-headline-large, 4rem)",
                fontWeight: "bold",
                color: "#1a1a1a",
              }}
            >
              문의 접수하기
            </h1>
            <p
              style={{
                fontSize: "var(--font-title-large, 1.8rem)",
                color: "#1a1a1a",
              }}
            >
              문의하실 내용을 작성해주세요.
            </p>
          </div>

          <div className="voc-status-link">
            <Link
              to="/voc/check"
              className="flex items-center justify-between transition-all hover:bg-[#ececec] hover:-translate-y-0.5 rounded-sm"
              style={{
                width: "190px",
                minHeight: "44px",
                padding: "0.5rem 1rem",
                border: "1px solid #888",
                backgroundColor: "#fff",
                fontSize: "var(--font-title-small, 1.5rem)",
                color: "#1a1a1a",
                textDecoration: "none",
              }}
            >
              문의 조회 페이지
              <span className="material-icons-round">arrow_outward</span>
            </Link>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-7 px-10 about:px-0" noValidate>
          {/* 서비스 구분 */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="service-type"
              className="after:content-['*'] after:text-red-500 after:ml-1 mb-[0.5rem]"
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                fontWeight: 500,
                color: "#1a1a1a",
              }}
            >
              채널 구분
            </label>
            <select
              id="service-type"
              name="serviceSortation"
              value={formData.serviceSortation}
              onChange={handleChange}
              required
              className="rounded-sm w-full transition-colors cursor-pointer focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
              style={{
                minHeight: "44px",
                padding: "0.75rem 1rem",
                paddingRight: "40px",
                border: "1px solid #e5e5e5",
                fontSize: "var(--font-title-small, 1.5rem)",
                backgroundColor: "#fafafa",
                appearance: "none",
                backgroundImage: `url('data:image/svg+xml;charset=UTF-8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8"><path fill="%23333" d="M6 8L0 2 1.5 0.5 6 5 10.5 0.5 12 2z"/></svg>')`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 16px center",
              }}
            >
              <option value="06">스탠다드 네트웍스</option>
              <option value="03">아레오</option>
              <option value="02">싸다오</option>
              <option value="04">네띠앙</option>
            </select>
          </div>

          {/* 작성자 & 연락처 */}
          <div className="flex flex-col sm:flex-row gap-5">
            <div className="flex flex-col flex-1 gap-1">
              <label
                htmlFor="name"
                className="after:content-['*'] after:text-red-500 after:ml-1 mb-[0.5rem]"
                style={{
                  fontSize: "var(--font-title-medium, 1.6rem)",
                  fontWeight: 500,
                  color: "#1a1a1a",
                }}
              >
                작성자
              </label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="이름"
                autoComplete="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2 rounded-sm"
                style={{
                  minHeight: "44px",
                  padding: "0.75rem 1rem",
                  border: "1px solid #e5e5e5",
                  fontSize: "var(--font-title-small, 1.5rem)",
                  backgroundColor: "#fafafa",
                }}
              />
            </div>
            <div className="flex flex-col flex-1 gap-1">
              <label
                htmlFor="phoneNumber"
                className="after:content-['*'] after:text-red-500 after:ml-1 mb-[0.5rem]"
                style={{
                  fontSize: "var(--font-title-medium, 1.6rem)",
                  fontWeight: 500,
                  color: "#1a1a1a",
                }}
              >
                연락처
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                placeholder="'-'를 제외한 숫자"
                autoComplete="tel"
                inputMode="numeric"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
                className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
                style={{
                  minHeight: "44px",
                  padding: "0.75rem 1rem",
                  border: "1px solid #e5e5e5",
                  fontSize: "var(--font-title-small, 1.5rem)",
                  backgroundColor: "#fafafa",
                }}
              />
            </div>
          </div>

          {/* 회사명 */}
          <div className="flex flex-col gap-1">
            <label
              className="mb-[0.5rem]"
              htmlFor="companyName"
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                fontWeight: 500,
                color: "#1a1a1a",
              }}
            >
              회사명
            </label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              placeholder="회사명을 입력해주세요. (선택)"
              value={formData.companyName}
              onChange={handleChange}
              className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
              style={{
                minHeight: "44px",
                padding: "0.75rem 1rem",
                border: "1px solid #e5e5e5",
                fontSize: "var(--font-title-small, 1.5rem)",
                backgroundColor: "#fafafa",
              }}
            />
          </div>

          {/* 비밀번호 */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="password"
              className="after:content-['*'] after:text-red-500 after:ml-1 mb-[0.5rem]"
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                fontWeight: 500,
                color: "#1a1a1a",
              }}
            >
              비밀번호
            </label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="6자리 미만 비밀번호"
              maxLength={6}
              autoComplete="off"
              value={formData.password}
              onChange={handleChange}
              required
              className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
              style={{
                minHeight: "44px",
                padding: "0.75rem 1rem",
                border: "1px solid #e5e5e5",
                fontSize: "var(--font-title-small, 1.5rem)",
                backgroundColor: "#fafafa",
              }}
            />
          </div>

          {/* 제목 */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="title"
              className="after:content-['*'] after:text-red-500 after:ml-1 mb-[0.5rem]"
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                fontWeight: 500,
                color: "#1a1a1a",
              }}
            >
              제목
            </label>
            <input
              type="text"
              id="title"
              name="title"
              placeholder="제목을 입력해주세요."
              value={formData.title}
              onChange={handleChange}
              required
              className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
              style={{
                minHeight: "44px",
                padding: "0.75rem 1rem",
                border: "1px solid #e5e5e5",
                fontSize: "var(--font-title-small, 1.5rem)",
                backgroundColor: "#fafafa",
              }}
            />
          </div>

          {/* 내용 */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="content"
              className="after:content-['*'] after:text-red-500 after:ml-1 mb-[0.5rem]"
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                fontWeight: 500,
                color: "#1a1a1a",
              }}
            >
              내용
            </label>
            <textarea
              id="content"
              name="content"
              rows={10}
              maxLength={800}
              placeholder="문의 내용을 800자 이내로 상세히 작성해주세요."
              value={formData.content}
              onChange={handleChange}
              required
              className="w-full transition-colors resize-y focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
              style={{
                minHeight: "180px",
                padding: "0.75rem 1rem",
                border: "1px solid #e5e5e5",
                borderRadius: "var(--radius-sm, 4px)",
                fontSize: "var(--font-title-small, 1.5rem)",
                backgroundColor: "#fafafa",
                fontFamily: "'Noto Sans KR', Roboto, sans-serif",
              }}
            ></textarea>
          </div>

          {/* 이메일 */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="email"
              className="after:content-['*'] after:text-red-500 after:ml-1 mb-[0.5rem]"
              style={{
                fontSize: "var(--font-title-medium, 1.6rem)",
                fontWeight: 500,
                color: "#1a1a1a",
              }}
            >
              이메일
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="답변을 받으실 이메일을 입력해주세요."
              autoComplete="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="rounded-sm w-full transition-colors focus:outline-none focus:border-navy focus-visible:outline-2 focus-visible:outline-navy focus-visible:outline-offset-2"
              style={{
                minHeight: "44px",
                padding: "0.75rem 1rem",
                border: "1px solid #e5e5e5",
                fontSize: "var(--font-title-small, 1.5rem)",
                backgroundColor: "#fafafa",
              }}
            />
          </div>

          {/* 첨부파일 */}
          <div
            className="flex items-start gap-4 flex-col sm:flex-row sm:items-center"
            style={{ width: "100%" }}
          >
            <div className="flex items-center justify-center gap-4 shrink-0">
              <label
                style={{
                  fontSize: "var(--font-title-medium, 1.6rem)",
                  fontWeight: 500,
                  color: "#1a1a1a",
                }}
              >
                첨부파일
              </label>
              <label
                htmlFor="attach-file"
                className="rounded-sm inline-block transition-colors cursor-pointer hover:bg-gray-100 hover:border-gray-400"
                style={{
                  padding: "0.5rem 0.75rem",
                  width: "90px",
                  minWidth: "max-content",
                  border: "1px solid #888",
                  backgroundColor: "#fff",
                  color: "#1a1a1a",
                  fontSize: "var(--font-label-small, 1.4rem)",
                  textAlign: "center",
                }}
              >
                첨부하기
              </label>
              <input
                type="file"
                id="attach-file"
                name="attach-file"
                accept=".zip"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden rounded-lg"
                aria-describedby="attach-file-info"
              />
            </div>
            <div
              className="flex gap-[10px] items-center"
              style={{
                fontSize: "var(--font-title-small, 1.5rem)",
                color: "#888",
              }}
            >
              {fileName && (
                <p className="file-name" style={{ color: "#1a1a1a" }}>
                  {fileName}
                </p>
              )}
              <p id="attach-file-info">
                .zip파일 형식만 가능합니다. 첨부파일의 크기를 20MB 이내로 첨부해주세요
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end mt-[10px]">
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-sm cursor-pointer transition-all hover:bg-navy hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              style={{
                fontSize: "var(--font-title-small, 1.6rem)",
                minHeight: "44px",
                padding: "0.5rem 2rem",
                backgroundColor: "#2A2A2F",
                color: "#fff",
              }}
            >
              {isLoading ? "등록 중..." : "등록하기"}
            </button>
          </div>
        </form>

        {/* Modal */}
        {showModal && (
          <div
            className="fixed inset-0 flex justify-center items-center z-[1000] px-[2rem] sm:px-0 "
            style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          >
            <div
              className="text-center bg-white p-[1.8rem_2rem] sm:p-[3rem_4rem]"
              style={{
                borderRadius: "var(--radius-md, 8px)",
                width: "400px",
                boxShadow: "0 5px 15px rgba(0, 0, 0, 0.3)",
              }}
            >
              <h3
                className="text-left pb-3 mb-[15px]"
                style={{
                  borderBottom: "1px solid #e5e5e5",
                  fontSize: "var(--font-title-large, 1.8rem)",
                  fontWeight: "bold",
                  color: "#1a1a1a",
                }}
              >
                {modalTitle}
              </h3>
              <p
                className="py-5 mb-[10px] leading-[1.5] whitespace-pre-wrap"
                style={{
                  fontSize: "var(--font-title-small, 1.5rem)",
                  color: "#1a1a1a",
                }}
              >
                {modalMessage}
              </p>
              <div className="flex justify-end gap-3">
                {isSuccess && (
                  <Link
                    to="/voc/check"
                    className="transition-colors cursor-pointer hover:bg-gray-600"
                    style={{
                      padding: "0.75rem 1.25rem",
                      minHeight: "44px",
                      backgroundColor: "#333",
                      color: "#fff",
                      border: "none",
                      borderRadius: "var(--radius-sm, 4px)",
                      fontSize: "var(--font-title-small, 1.6rem)",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    조회 페이지로 이동
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="transition-colors cursor-pointer hover:bg-black p-[10px_20px] rounded-sm"
                  style={{
                    minHeight: "44px",
                    backgroundColor: "#032C6D",
                    color: "#fff",
                    border: "none",
                    fontSize: "var(--font-title-small, 1.5rem)",
                  }}
                >
                  확인
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default VocSubmit;
